// The swap card's pure logic: the amount field, max, the reverse button, slippage, and turning
// errors into something a user can act on.

import type { Delivery } from "./api";
import { CHAINS, knownSrcToken, knownToken, type Chain, type ChainKey, type JupnetRoute, type Token } from "./chains";

/** What the card selects; `delivery: gum_deposit` means "To: Jupnet" through `dst`. */
export type Selection = { src: ChainKey; srcToken: string; dst: ChainKey; dstToken: string; delivery: Delivery };

/** A typed amount, kept to what an amount can be: digits and one decimal point (a comma is one). */
export function cleanAmount(s: string): string {
  let t = s.replace(/,/g, ".").replace(/[^\d.]/g, "");
  const dot = t.indexOf(".");
  if (dot >= 0) t = t.slice(0, dot + 1) + t.slice(dot + 1).replace(/\./g, "");
  if (t.startsWith(".")) t = `0${t}`;
  return t.replace(/^0+(?=\d)/, "");
}

/** Gas kept back when "max" spends a native token, per chain (smallest units). */
const GAS_RESERVE: Partial<Record<ChainKey, bigint>> = {
  ethereum: 3_000_000_000_000_000n, // 0.003 ETH
  bsc: 1_000_000_000_000_000n, // 0.001 BNB
  base: 200_000_000_000_000n, // 0.0002 ETH
  arbitrum: 200_000_000_000_000n,
  robinhood: 200_000_000_000_000n,
  solana: 10_000_000n, // 0.01 SOL: fees and the accounts the open creates
  sui: 50_000_000n, // 0.05 SUI
};

/** The most "max" fills in: the whole balance of a token, a native one less its gas reserve. */
export function maxSpendable(balance: bigint, token: Token, chain: Chain): bigint {
  if (!token.native) return balance;
  const left = balance - (GAS_RESERVE[chain.key] ?? 0n);
  return left > 0n ? left : 0n;
}

/**
 * The reverse button: what was received is paid, what was paid is received. Null when the other
 * way round is not a route the demo offers (Sui sources pay its stable only, Jupnet is a source
 * only once the api has its program, a token the other side has no preset for falls back to that
 * side's first).
 */
export function reversed(s: Selection, ctx: { jupnetSrc: Chain | null; routes: JupnetRoute[] | null }): Selection | null {
  const srcChain = s.src === "jupnet" ? ctx.jupnetSrc : CHAINS[s.src];
  if (!srcChain) return null;
  if (s.delivery === "gum_deposit") {
    // Jupnet → back where it came from
    const j = ctx.jupnetSrc;
    if (!j?.enabled || s.src === "jupnet") return null;
    const dstTok = knownToken(srcChain, s.srcToken)?.addr ?? srcChain.dstTokens[0]?.addr;
    if (!dstTok) return null;
    return { src: "jupnet", srcToken: j.srcTokens[0]!.addr, dst: s.src, dstToken: dstTok, delivery: "direct" };
  }
  const dstChain = CHAINS[s.dst];
  if (!dstChain.enabled) return null;
  const newSrcToken = knownSrcToken(dstChain, s.dstToken)?.addr ?? dstChain.srcTokens[0]!.addr;
  if (s.src === "jupnet") {
    // a withdrawal reversed is a deposit to Jupnet, through another chain than the one paying
    const route = ctx.routes?.find((r) => r.chain.key !== s.dst);
    if (!route) return null;
    return { src: s.dst, srcToken: newSrcToken, dst: route.chain.key, dstToken: route.tokens[0]!.addr, delivery: "gum_deposit" };
  }
  if (!srcChain.enabled) return null;
  const newDstToken = knownToken(srcChain, s.srcToken)?.addr ?? srcChain.dstTokens[0]?.addr;
  if (!newDstToken) return null;
  return { src: s.dst, srcToken: newSrcToken, dst: s.src, dstToken: newDstToken, delivery: "direct" };
}

/** Delivery slippage presets (bps) and the bounds of a custom value. */
export const SLIPPAGE_PRESETS = [50, 100, 200];
export const SLIPPAGE_MIN_BPS = 1;
export const SLIPPAGE_MAX_BPS = 1000;
/** The hosted swap's tolerance; `undefined` = the api's default ("auto"). */
export const SRC_SLIPPAGE_PRESETS: (number | undefined)[] = [undefined, 30, 100, 300];

/** A custom slippage typed in percent → bps, or null when not a number within the bounds. */
export function parseSlippage(pct: string): number | null {
  const t = pct.trim().replace(",", ".");
  if (!/^\d{0,2}(\.\d{0,2})?$/.test(t) || t === "" || t === ".") return null;
  const bps = Math.round(Number(t) * 100);
  return bps >= SLIPPAGE_MIN_BPS && bps <= SLIPPAGE_MAX_BPS ? bps : null;
}

export const fmtPct = (bps: number) => `${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}%`;

/** An error as the user should read it: a headline they can act on, and the original as detail
 *  when the headline replaced it. */
export function friendlyError(raw: string): { title: string; detail?: string } {
  const m = raw.trim();
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(m))
    return { title: "Can't reach the GUM Solver Intent Model api. Check your connection and try again.", detail: m };
  if (/you rejected/i.test(m)) return { title: cap(m.replace(/^.*?(you rejected)/i, "$1")) + "." };
  if (/upstream_unavailable|did not answer|503/i.test(m)) return { title: "A chain or the swap aggregator didn't answer. Try again in a moment.", detail: m };
  if (/price_moved|price moved/i.test(m)) return { title: "The swap price moved. Get a fresh quote and try again.", detail: m };
  if (/bundle_not_landed|bundle_failed|bundle_rejected|broadcast_rejected|simulation_failed/i.test(m))
    return { title: "The transaction did not land and nothing was spent. Swap again to retry.", detail: m };
  if (/would revert/i.test(m)) return { title: "This swap would fail on chain, so it was not sent. Get a fresh quote and retry.", detail: m };
  return { title: cap(m) };
}
